---
id: "deep-research-prompts"
title: "5 Deep Research Prompts that are Supercharging our Sales Strategy"
excerpt: "We’ve been using deep research to streamline our sales efforts, and the results have been game-changing. Here’s what we’ve learned."
date: "Feb 19, 2025"
category: "Basics"
readTime: "6"
author: "Daniel Bolus"
featured: false
---
Sales isn’t about throwing darts in the dark—it’s about precision. The best teams aren’t just working harder; they’re leveraging cutting-edge tools to work smarter. That’s where OpenAI’s new deep research agent (among others) comes in. Think of it as your personal research assistant, surfacing insights you didn’t even know existed.

We’ve been using deep research to streamline our sales efforts, and the results have been game-changing. Here’s what we’ve learned.

> ### **In this post -**
>
> 1. Conference prospects
>
> 2. Warm intros
>
> 3. Meeting prep
>
> 4. Benchmark content
>
> 5. Discover new markets

## What deep research does well (and where it falls short)

### Where it shines:

- **Digging up niche info:** Deep research is incredibly effective at unearthing hard-to-find company data—info buried in blog posts, obscure documentation, and hidden corners of the internet.

- **Analyzing public data:** If it's publicly available, deep research can find it, synthesize it, and surface the most relevant bits.

- **Contextualizing information:** Give it your website or a company profile, and it will make connections that help frame insights in a way that actually matters to your business.


### Where it struggles:

- **People:** It can’t scrape LinkedIn, making it difficult to find specific individuals.

- **Timelines:** Some sources aren’t up-to-date, so you might need to cross-check time-sensitive information.

- **Source selection can vary:** If one source dominates the search, you may need to prompt it to cast a wider net.


Now, let’s talk tactics. Here are five prompts we’re using to refine our sales strategy:

## 1\. Identifying high-value prospects at conferences

Conferences are great for networking, but not every company on the attendee list is a good fit. Instead of guessing, we’re using deep research to help prioritize outreach.

**Prompt:**

> Given this list of companies: \[conference list\], identify the top 15 that align with \[our company’s ICP\]. Focus on firms that have recently expanded, secured funding, or signaled interest in \[our product category\].

The result? A targeted prospect list that makes conference outreach way more efficient.

**OpenPipe Example:**

Prompt:

> Given this list of companies: [https://www.generativeaiexpo.com/east/](https://www.generativeaiexpo.com/east/), identify the top 15 that align with OpenPipe’s ICP: enterprise customers looking to fine-tune production LLMs. They must already have SOTA models like GPT-4o deployed in production at scale, and are looking for improved accuracy, lower latency, and lower costs. Focus on firms that have recently expanded, secured funding, or signaled interest in fine-tuning LLMs.

Output Highlight:

> **Kaseya**, a heavyweight in IT service management, recently launched **Kaseya 365**, an **AI-driven platform** unifying IT and security operations. This platform leverages AI to automate tasks – Kaseya reports it has **automated over 20 million hours of work** for IT teams. Post-acquisition of companies like Datto, Kaseya has scaled up to serve 45,000+ customers and manage “billions of device,” so any AI it deploys (such as GPT-based analysis or recommendations) operates at huge scale. Kaseya is using AI to reduce cost and complexity for MSPs and IT departments, which implies constant tuning and optimization of its models. Its rapid expansion and integration of AI suggest a willingness to fine-tune and customize models (possibly using customer IT data) to further improve accuracy in automation and deliver better ROI to its clients.

## 2\. Finding warm intros

A cold email is fine. A warm intro? Way better. We use deep research to find companies already connected to our network.

**Prompt:**

> Search through this list of companies: \[investor’s portfolio companies\]. Identify the ones that fit \[our ICP\]. For each company, craft a forwarded email that \[our company\] can send \[investor\] asking for an intro. The email should be no longer than a few sentences and should highlight why we are a good fit.

Instead of generic outreach, we can now approach companies with a built-in advantage—higher response rates, better conversations, and warmer leads.

**OpenPipe Example:**

Prompt:

> Search through this list of companies: [https://costanoa.vc/companies/](https://costanoa.vc/companies/). Identify the ones that fit OpenPipe’s ICP: enterprise customers looking to fine-tune production LLMs. They must already have SOTA models like GPT-4o deployed in production at scale, and are looking for improved accuracy, lower latency, and lower costs. For each company, craft a forwarded email that OpenPipe can send Costanoa asking for an intro. The email should be no longer than a few sentences and should highlight why we are a good fit.

Output Highlight:

> **Replicant Forwardable Email:**
>
> Hi – Could you introduce OpenPipe to Replicant? Replicant is a pioneer in using GPT-quality LLMs to automate customer calls. We believe we can **help them go further**: OpenPipe’s platform would let Replicant **train a bespoke language model on their call data**, yielding **more accurate, faster responses** for customers and reducing costly GPT-4 API usage. For a high-call-volume solution like Replicant, our fine-tuned models could significantly **improve response latency and cut operating costs** without sacrificing quality.

## 3\. Prepping for meetings like a pro

Showing up to a sales call without context can be a waste of everyone’s time. We use deep research to know exactly what’s happening inside a prospect’s company before we even hop on the call.

**Prompt:**

> Provide a list of all vendors \[target company\] is working with for \[specific use case\]. Pull relevant updates from the last 9 months (prioritizing the last 2 months). Identify potential gaps where \[our product\] could add value and suggest which business units to engage.

Armed with this intel, we go into meetings with tailored pitches that actually resonate.

**OpenPipe Example:**

Prompt:

> Provide a list of all vendors Kaseya is working with for fine-tuning LLMs. Pull relevant updates from the last 9 months (prioritizing the last 2 months). Identify potential gaps where OpenPipe’s product could add value and suggest which business units to engage.

Output Highlight:

> **PSA (Professional Services Automation) & Service Desk Team**
>
> This unit oversees **Kaseya BMS and Vorex,** the ticketing and customer support systems used by MSPs. They’ve recently launched **Cooper Copilot** features to assist technicians with ticket summaries, response drafting, and auto-resolution notes. Engaging this team is key because they directly tackle **customer support AI**. OpenPipe could help them fine-tune the language model on helpdesk data to increase the accuracy of summaries or to better understand IT-specific queries. Business impact: more efficient ticket handling and faster resolution times for MSP support teams, which is a major **value proposition to end-customers**. By working with the PSA product managers and engineering leads, OpenPipe can demonstrate how fine-tuning will reduce the manual effort per ticket (through even smarter automation) and improve end-user satisfaction. Since this team’s goal is to streamline service desk workflows, they would welcome improvements that make the AI feel \*\*like an expert MSP technician on day one.

## 4\. Benchmarking our website against the best

Your website is your first impression. If it’s not conveying the right information for your target ICP, it’s failing. We use deep research to analyze what’s working in the industry and apply those insights to our own site.

**Prompt:**

> Analyze landing pages from these companies: \[list of industry-leading companies\]. Identify common elements that drive engagement and conversion. Provide five specific improvements for \[our website\], backed by examples from high-performing sites.

This ensures our messaging, design, and CTAs are optimized for maximum impact.

**OpenPipe Example:**

Prompt:

> Analyze landing pages from the top 15 leading companies who sell enterprise AI solutions. Identify common elements that drive engagement and conversion. Provide five specific improvements for [https://openpipe.ai/](https://openpipe.ai/), backed by examples from high-performing sites.

Output Highlight:

> **Showcase Case Studies or Detailed Success Stories: Current State:** While testimonials are present, in-depth case studies are lacking. **Improvement:** Develop detailed case studies that outline specific challenges, solutions provided by OpenPipe, and measurable outcomes. **Example:** Other companies highlight their collaboration with leading organizations, which adds credibility.

## 5\. Discovering new use cases and market opportunities

Sometimes the best opportunities are the ones you’re not even thinking about. We use deep research to uncover adjacent use cases that could open up new revenue streams.

**Prompt:**

> Find 20 case studies with cited sources where companies have successfully used \[our product’s offering\] to solve \[specific problem\]. Focus on sources from Q3 2024 - Q1 2025.

This helps us spot emerging trends, refine our positioning, and expand into new markets with confidence.

**OpenPipe Example:**

Prompt:

> Find 20 case studies with cited sources where enterprises have successfully used fine-tuned LLMs in production to get greater accuracy and reduce costs. Focus on sources from Q3 2024 - Q1 2025.

Output Highlight:

> **Pharmaceutical R&D (Drug Discovery)** _Google Research & DeepMind_ developed **Tx-LLM**, a large model fine-tuned from PaLM-2 on 709 biomedical datasets covering 66 drug discovery tasks. The fine-tuned Tx-LLM performed **at or above the state-of-the-art on 43 of 66 tasks**, and even **exceeded all prior models on 22 tasks**. By specializing a general model to the pharma domain, they improved prediction accuracy for molecule properties, target efficacy, and safety, potentially accelerating drug development while avoiding the costs of multiple niche models.

## Takeaways

Deep research is more than just a productivity hack—it’s a strategic advantage. Whether it’s refining prospect lists, preparing for meetings, or optimizing landing pages, these prompts have given us a sharper edge in sales.

If you’re not using deep research yet, now’s the time to start. The difference is night and day.

Curious how to leverage other cutting-edge LLM technologies (like fine-tuning your own model!) to gain a competitive advantage? [Sign up for an OpenPipe account](https://app.openpipe.ai/) or subscribe to our newsletter below to join our growing community!